import { Component, OnInit } from '@angular/core'
import { Checkout, Depot } from 'depoto-core/src/entities'
import { ActivatedRoute, Router } from '@angular/router'
import { CoreService, NotificationService } from '../../services'
import { Institution } from 'depoto-core/src/entities/Institution'
import { HistoryUtil, SchemaUtil } from '../../utils'
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'

import * as Sentry from '@sentry/angular-ivy'

@Component({
  selector: 'app-institution',
  templateUrl: './institution.component.html',
  styleUrls: ['./institution.component.scss'],
})
export class InstitutionComponent implements OnInit {
  institution: Institution
  checkouts: Checkout[]
  depots: Depot[]
  isCreating = false
  loading = true
  submitted = false

  institutionForm = new UntypedFormGroup({
    name: new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
    checkouts: new UntypedFormControl({ value: [], disabled: false }, []),
    depots: new UntypedFormControl({ value: [], disabled: false }, []),
  })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private core: CoreService,
  ) {}

  ngOnInit() {
    const id = this.route.snapshot.params['id']
    if (isNaN(id)) {
      this.isCreating = true
      this.institution = new Institution()
      this.refreshData(null)
    } else {
      this.refreshData(id)
    }
  }

  hasUnsavedDataChanges(): boolean {
    return false
  }

  fillFormValues() {
    this.institutionForm.setValue({
      name: this.institution.name,
      checkouts: this.institution.checkouts,
      depots: this.institution.depots,
    })
  }

  getFormValue() {
    this.institution.name = this.institutionForm.value.name
    this.institution.checkouts = this.institutionForm.value.checkouts
    this.institution.depots = this.institutionForm.value.depots
  }

  async refreshData(id?: number) {
    this.loading = true
    this.depots = this.core.lists.depots
    this.checkouts = this.core.lists.checkouts

    if (id && id > 0) {
      this.institution = await this.core.services.institution.getById(id, SchemaUtil.institution)
      this.fillFormValues()
    }
    this.loading = false
  }

  async createInstitution() {
    this.submitted = true
    if (this.institutionForm.invalid) {
      return
    }
    this.loading = true
    try {
      this.getFormValue()
      const result = await this.core.services.institution.create(this.institution)
      this.notificationService.success(this.translateService.instant('alert.institution.created'))
      this.loading = false
      if (result) {
        this.router.navigate(['/institution/', result.id])
      }
    } catch (e) {
      console.log(e)
    }
  }

  async updateInstitution() {
    this.submitted = true
    if (this.institutionForm.invalid) {
      return
    }
    try {
      this.loading = true
      this.getFormValue()
      const result = await this.core.services.institution.update(this.institution)
      this.notificationService.success(this.translateService.instant('alert.institution.updated'))
      if (result.id > 0) {
        return this.refreshData(result.id)
      }
    } catch (err) {
      console.log(err)
    } finally {
      this.loading = false
    }
  }

  async deleteInstitution() {
    const confirmation = confirm(this.translateService.instant('institution.delete.confirmation'))
    if (confirmation) {
      try {
        await this.core.services.institution.delete(this.institution)
        this.notificationService.success(this.translateService.instant('alert.institution.deleted'))
        HistoryUtil.navigateBack()
      } catch (err) {
        console.log(err)
      }
    }
  }

  isChosenCheckout(checkout: Checkout): boolean {
    let exists = false
    this.institution.checkouts.forEach((ch: Checkout) => {
      if (ch.id === checkout.id && ch.name === checkout.name) {
        exists = true
        return
      }
    })
    return exists
  }

  toggleCheckout(checkout: Checkout, event: any) {
    if (event.target.checked) {
      this.institutionForm.value.checkouts.push(checkout)
    } else {
      this.institution.checkouts.forEach((ch: Checkout, i: number) => {
        if (checkout.id === ch.id && checkout.name === ch.name) {
          this.institutionForm.value.checkouts.splice(i, 1)
        }
      })
    }
  }

  isChosenDepot(depot: Depot): boolean {
    let exists = false
    this.institution.depots.forEach((d: Depot) => {
      if (d.id === depot.id && d.name === depot.name) {
        exists = true
        return
      }
    })
    return exists
  }

  toggleDepot(depot: Depot, event: any) {
    if (event.target.checked) {
      this.institutionForm.value.depots.push(depot)
    } else {
      this.institution.depots.forEach((d: Depot, i: number) => {
        if (depot.id === d.id && depot.name === d.name) {
          this.institutionForm.value.depots.splice(i, 1)
        }
      })
    }
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.institutionForm.controls
  }

  triggerBackBtn() {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      this.router.navigate(['/institutions'])
    }
  }
}
